import { useState, useEffect } from 'react'
import {
  VStack,
  Heading,
  Text,
  Box,
  HStack,
  Stack,
  Flex,
  createToaster,
} from '@chakra-ui/react'
import { Switch } from '@chakra-ui/react'
import { FileText, CalendarDays, AlertCircle, CheckCircle2 } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { useQueryClient } from '@tanstack/react-query'

const toaster = createToaster({
  placement: 'top-end',
  pauseOnPageIdle: true,
})

interface Residence {
  id: string
  name: string
  residence_key: string | null
}

export function DocumentTemplatesPage() {
  const { data: authData } = useAuth()
  const queryClient = useQueryClient()
  const [isLoading, setIsLoading] = useState(false)
  const [residences, setResidences] = useState<Residence[]>([])
  const [annualizationEnabled, setAnnualizationEnabled] = useState(false)

  // Load residences and agency settings
  useEffect(() => {
    const loadData = async () => {
      if (!authData?.profile?.id) return

      try {
        // Get user's residences through user_residences
        const { data: userResidences, error: urError } = await supabase
          .from('user_residences')
          .select('residence_id')
          .eq('user_id', authData.profile.id)

        if (urError) throw urError

        const residenceIds = userResidences?.map((ur) => ur.residence_id) || []

        if (residenceIds.length === 0) {
          setResidences([])
          return
        }

        // Get residence details
        const { data: residencesData, error: resError } = await supabase
          .from('residences')
          .select('id, name, residence_key, agency_id')
          .in('id', residenceIds)

        if (resError) throw resError

        setResidences(residencesData || [])

        // Get agency annualization setting (from first residence's agency)
        if (residencesData && residencesData.length > 0 && residencesData[0]) {
          const { data: agencyData, error: agencyError } = await supabase
            .from('agencies')
            .select('auto_create_year_folders')
            .eq('id', residencesData[0].agency_id)
            .single()

          if (agencyError) throw agencyError

          const enabled = agencyData?.auto_create_year_folders ?? false
          console.log('📊 État initial annualisation:', {
            agencyId: residencesData[0].agency_id,
            auto_create_year_folders: enabled,
          })
          setAnnualizationEnabled(enabled)
        }
      } catch (error) {
        console.error('Error loading data:', error)
        toaster.error({
          title: 'Erreur',
          description: 'Impossible de charger les données',
        })
      }
    }

    loadData()
  }, [authData])

  const handleToggleAnnualization = async (checked: boolean) => {
    if (residences.length === 0) return

    console.log('🔄 Toggle annualization:', {
      checked,
      action: checked ? 'ACTIVER (créer dossiers)' : 'DÉSACTIVER (supprimer dossiers)',
      residencesCount: residences.length,
      currentState: annualizationEnabled,
    })

    setIsLoading(true)

    try {
      let successCount = 0
      let errorCount = 0
      const errors: string[] = []

      // Apply toggle to each residence
      for (const residence of residences) {
        console.log(`  → Traitement résidence "${residence.name}" avec p_enable=${checked}`)

        const { data, error } = await supabase.rpc('toggle_year_folders_for_residence', {
          p_residence_id: residence.id,
          p_enable: checked,
        })

        if (error) {
          console.error(`    ❌ Erreur SQL:`, error)
          errorCount++
          errors.push(`${residence.name}: ${error.message}`)
          continue
        }

        // Parse JSONB response
        const result = data as {
          success: boolean
          message: string
          error?: string
          documents_count?: number
          deleted_count?: number
          total_found?: number
        }

        console.log(`    ✅ Résultat SQL:`, result)

        if (result.success) {
          successCount++
          // Log detailed info for deletion
          if (!checked && result.deleted_count !== undefined && result.total_found !== undefined) {
            console.log(`      📁 Dossiers supprimés: ${result.deleted_count}/${result.total_found}`)
          }
        } else {
          errorCount++
          // Special handling for cannot disable with documents
          if (result.error === 'cannot_disable_with_documents') {
            const docCount = result.documents_count || 0
            errors.push(
              `${residence.name}: ${docCount} document(s) présent(s) dans les dossiers année. ` +
              `Veuillez d'abord les déplacer ou les supprimer.`
            )
          } else {
            errors.push(`${residence.name}: ${result.error || result.message}`)
          }
        }
      }

      // Update agency setting if at least one succeeded
      if (successCount > 0) {
        const agencyId = residences[0] ?
          (await supabase
            .from('residences')
            .select('agency_id')
            .eq('id', residences[0].id)
            .single()).data?.agency_id
          : null

        if (agencyId) {
          await supabase
            .from('agencies')
            .update({ auto_create_year_folders: checked })
            .eq('id', agencyId)

          setAnnualizationEnabled(checked)

          // 🔄 CRUCIAL: Invalider le cache pour forcer le rechargement des dossiers
          // Cela permet de voir la suppression des dossiers année dans l'UI
          residences.forEach((residence) => {
            queryClient.invalidateQueries({ queryKey: ['document-folders', residence.id] })
          })
          console.log('✅ Cache invalidé pour toutes les résidences')
        }
      }

      // Show results
      if (errorCount === 0) {
        toaster.success({
          title: 'Succès',
          description: `Annualisation ${checked ? 'activée' : 'désactivée'} pour ${successCount} résidence(s)`,
        })
      } else if (successCount > 0) {
        toaster.warning({
          title: 'Partiellement appliqué',
          description: `${successCount} résidence(s) modifiée(s), ${errorCount} erreur(s)\n${errors.join('\n')}`,
        })
      } else {
        toaster.error({
          title: 'Erreur',
          description: `Impossible de modifier les résidences:\n${errors.join('\n')}`,
        })
      }
    } catch (error) {
      console.error('Error toggling annualization:', error)
      toaster.error({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Une erreur est survenue',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const currentYear = new Date().getFullYear()

  return (
    <VStack align="stretch" gap={6} w="full" maxW="full">
      {/* Header */}
      <VStack align="start" gap={2} w="full">
        <HStack gap={2}>
          <FileText size={24} />
          <Heading as="h2" fontSize="2xl">
            Documents et modèles
          </Heading>
        </HStack>
        <Text color="gray.600">
          Paramètres d'organisation des documents et personnalisation des modèles
        </Text>
      </VStack>

      {/* Annualization Section */}
      <Box
        p={6}
        bg="white"
        borderRadius="lg"
        border="1px solid"
        borderColor="gray.200"
        boxShadow="sm"
        w="full"
      >
        <VStack align="stretch" gap={4} w="full">
          {/* Header */}
          <Flex justify="space-between" align="center" w="full">
            <Box>
              <HStack gap={2} mb={1}>
                <CalendarDays size={20} />
                <Heading fontSize="lg">Annualisation automatique</Heading>
              </HStack>
              <Text fontSize="sm" color="gray.600">
                Crée automatiquement des dossiers par année dans chaque catégorie système
              </Text>
            </Box>
            <Switch.Root
              size="lg"
              checked={annualizationEnabled}
              disabled={isLoading || residences.length === 0}
              onCheckedChange={(details: { checked: boolean }) =>
                handleToggleAnnualization(details.checked)
              }
            >
              <Switch.Control>
                <Switch.Thumb />
              </Switch.Control>
              <Switch.HiddenInput />
            </Switch.Root>
          </Flex>

          {/* Info box when enabled */}
          {annualizationEnabled && (
            <Box
              p={3}
              bg="blue.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="blue.200"
            >
              <HStack gap={2} mb={2}>
                <CheckCircle2 size={16} color="#3182CE" />
                <Text fontSize="xs" fontWeight="semibold" color="blue.700">
                  Années créées automatiquement
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.700">
                {currentYear - 1}, {currentYear}, {currentYear + 1}
              </Text>
              <Text fontSize="xs" color="gray.600" mt={2}>
                Le dossier {currentYear + 2} sera créé automatiquement en janvier {currentYear + 1}
              </Text>
            </Box>
          )}

          {/* Info box when disabled */}
          {!annualizationEnabled && (
            <Box
              p={3}
              bg="gray.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <HStack gap={2} mb={1}>
                <AlertCircle size={16} color="#718096" />
                <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                  Organisation par catégorie uniquement
                </Text>
              </HStack>
              <Text fontSize="xs" color="gray.600">
                Les documents seront organisés directement dans les catégories sans sous-dossiers d'année
              </Text>
            </Box>
          )}

          {/* Warning about disabling */}
          {annualizationEnabled && (
            <Box
              p={3}
              bg="orange.50"
              borderRadius="md"
              borderWidth="1px"
              borderColor="orange.200"
            >
              <HStack gap={2} align="start">
                <Box flexShrink={0}>
                  <AlertCircle size={16} color="#DD6B20" />
                </Box>
                <Stack gap={1} flex={1}>
                  <Text fontSize="xs" fontWeight="semibold" color="orange.700">
                    Important
                  </Text>
                  <Text fontSize="xs" color="gray.700">
                    La désactivation ne pourra se faire que si les dossiers d'année sont vides. Si des documents sont
                    présents, vous devrez d'abord les déplacer ou les supprimer.
                  </Text>
                </Stack>
              </HStack>
            </Box>
          )}
        </VStack>
      </Box>

      {/* Future: Templates customization section */}
      <Box
        p={12}
        bg="gray.50"
        borderRadius="lg"
        border="1px dashed"
        borderColor="gray.300"
        textAlign="center"
        w="full"
      >
        <VStack gap={3}>
          <FileText size={48} color="#9ca3af" />
          <VStack gap={2}>
            <Heading fontSize="lg" color="gray.700">
              Personnalisation des modèles
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Bientôt disponible : personnalisez vos modèles de documents, courriers et convocations
            </Text>
          </VStack>
        </VStack>
      </Box>
    </VStack>
  )
}
